import React from "react";
// import styled from "@emotion/styled";
import gql from "graphql-tag";
import { Box } from "@atoms";
import { OccasionQuery_Occasion_collections_edges_node_links as ILink } from "../../../pages/occasionComponents/OccasionView/__generated__/OccasionQuery";
import { CollectionThreeUp } from "../CollectionThreeUp";
import { CollectionTwoUp } from "../CollectionTwoUp";
import { CollectionFourUp } from "../CollectionFourUp";
import { CollectionCollage } from "../CollectionCollage";
import { CollectionGrid } from "../CollectionGrid";
import { CollectionTitle } from "../CollectionTitle";
import { CollectionCarousel } from "../CollectionCarousel";
import { CollectionDepartmentGrid } from "../CollectionDepartmentGrid";
import { CollectionFeatured } from "../CollectionFeatured";
import { CollectionBanner } from "../CollectionBanner";
import { CollectionGridQuarterBanner } from "../CollectionGridQuarterBanner";
import { CollectionBrandCarousel } from "../CollectionBrandCarousel";
import { CollectionSingle } from "../CollectionSingle";
import CollectionNearbyBrandsQuery from "../CollectionNearbyBrands/CollectionNearbyBrandsQuery";
import { CollectionRenderFragment } from "./__generated__/CollectionRenderFragment";
// == Types ================================================================

export interface IAssetsType {
  description: string | null;
  destination: string | null;
  image: string | null;
  title: string | null;
}

export interface ICollectionRenderProps {
  collection: {
    style: string | null;
    assets?: IAssetsType[];
    links: (ILink | null)[];
  };
}

interface IProps {
  collection: CollectionRenderFragment;
  showTopSpacing: boolean;
}

// == Constants ============================================================

export const COLLECTION_TOP_SPACING = [4, 4, "40px"];

const FRAGMENTS = {
  fields: gql`
    fragment CollectionRenderFragment on Collection {
      id
      name
      style
      description
      headerDescription
      headerImage
      linksCount
      showTitle
      links(first: $carouselItems) {
        id
        type
        giftcardType
        name
        image
        logoImage
        giveSentence
        terms
        promoLabel
        promoDescription
        promoSecondaryLabel
        promoEndsAt
      }
      ...CollectionFourUpFragment
      ...CollectionThreeUpFragment
      ...CollectionTwoUpFragment
      ...CollectionBrandCarouselFragment
      ...CollectionCarouselFragment
    }
    ${CollectionFourUp.fragments.fields}
    ${CollectionThreeUp.fragments.fields}
    ${CollectionTwoUp.fragments.fields}
    ${CollectionBrandCarousel.fragments.fields}
    ${CollectionCarousel.fragments.fields}
  `,
};

const ACCEPTED_COLLECTIONS = [
  "FEATURED",
  "GRID",
  "CAROUSEL",
  "COLLAGE",
  "TWO_UP",
  "THREE_UP",
  "FOUR_UP",
  "BANNER",
  "BANNER_CAROUSEL",
  "BRAND_CAROUSEL",
  "DEPARTMENT_GRID",
  "GRID_QUARTER_BANNER",
  "GRID_HALF_BANNER",
  "SINGLE",
  "NEARBY_BRANDS",
];

// == Component ============================================================

const renderCollectionTitle = (collection) => {
  if (!collection.showTitle) return null;

  const onCollectionPress = () => {
    window.location.href = `/collections/${collection.id}`;
  };

  if (["NEARBY_BRANDS"].includes(collection.style)) return null;
  if (collection.style === "BRAND_CAROUSEL" && collection.brands.edges.length === 0) return null;
  if (["GRID", "CAROUSEL", "FEATURED"].includes(collection.style)) {
    return (
      <CollectionTitle
        collectionId={collection.id}
        count={collection.linksCount}
        title={collection.name}
        onTitlePress={onCollectionPress}
      />
    );
  }
  if (collection.style === "BRAND_CAROUSEL" && collection.brands.edges.length > 0) {
    return (
      <CollectionTitle
        collectionId={collection.id}
        count={collection.brands.edges.length}
        title={collection.name}
        onTitlePress={onCollectionPress}
      />
    );
  }
  return (
    <CollectionTitle
      collectionId={collection.id}
      title={collection.name}
      onTitlePress={onCollectionPress}
    />
  );
};

const renderCollection = (collection) => {
  switch (collection.style) {
    case "CAROUSEL":
      return <CollectionCarousel collection={collection} />;
    case "NEARBY_BRANDS":
      return <CollectionNearbyBrandsQuery collection={collection} />;
    case "COLLAGE":
      return <CollectionCollage collection={collection} />;
    case "DEPARTMENT_GRID":
      return <CollectionDepartmentGrid collection={collection} />;
    case "FEATURED":
      return <CollectionFeatured collection={collection} />;
    case "TWO_UP":
      return <CollectionTwoUp collection={collection} />;
    case "FOUR_UP":
      return <CollectionFourUp collection={collection} />;
    case "GRID":
      return <CollectionGrid collection={collection} />;
    case "THREE_UP":
      return <CollectionThreeUp collection={collection} />;
    case "BANNER_CAROUSEL":
    case "BANNER":
      return <CollectionBanner collection={collection} />;
    case "GRID_QUARTER_BANNER":
      return <CollectionGridQuarterBanner collection={collection} />;
    case "BRAND_CAROUSEL":
      return <CollectionBrandCarousel collection={collection} />;
    case "SINGLE":
      return <CollectionSingle collection={collection} />;
    default:
      return null;
  }
};

const CollectionRender = ({ collection, showTopSpacing }: IProps) => {
  if (!ACCEPTED_COLLECTIONS.includes(collection.style)) return null;
  return (
    <Box display="block" mt={showTopSpacing ? COLLECTION_TOP_SPACING : 0}>
      {renderCollectionTitle(collection)}
      {renderCollection(collection)}
    </Box>
  );
};

CollectionRender.fragments = FRAGMENTS;

export default CollectionRender;

// == Styles ===============================================================
